import React, { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import MedicalDecor from "../components/MedicalDecor"
import CostSavingCalculator from "../components/CostSavingCalculator"
import JobShareButton from "../components/JobShareButton"
import { getAPIBaseURL } from "../services/helpers"
import { getData } from "../services/request"
import { employmentTypeLabel, locationTypeLabel } from "../utils/employmeLabels"
import logo from "../asset/images/logo.svg"

const Landing = () => {
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showCookieBanner, setShowCookieBanner] = useState(false)
  const [jobs, setJobs] = useState([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [jobsError, setJobsError] = useState("")

  useEffect(() => {
    const fetchJobs = async () => {
      setJobsLoading(true)
      setJobsError("")
      const res = await getData(`${getAPIBaseURL()}/employme/jobs`, {}, {})
      setJobsLoading(false)
      if (res.error === "" && res.result?.status === 200) {
        setJobs(res.result.data?.data || [])
        return
      }
      setJobsError("Unable to load jobs right now.")
    }
    fetchJobs()
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)

    try {
      const consent = window.localStorage.getItem('employme_cookie_consent') || window.localStorage.getItem('medpool_cookie_consent')
      setShowCookieBanner(consent !== 'accepted')
    } catch (e) {
      setShowCookieBanner(true)
    }

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        isScrolled 
          ? 'border-gray-200 bg-white/98 backdrop-blur-md shadow-sm' 
          : 'border-transparent bg-white/95 backdrop-blur-sm'
      }`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <Link 
              to="/" 
              className="group flex items-center gap-2 transition-all duration-300 hover:scale-105"
            >
              <div className="relative">
                <img 
                  className="h-8 w-8 transition-transform duration-300 group-hover:rotate-6" 
                  src={logo} 
                  alt="Employ Me logo" 
                />
                <div className="absolute inset-0 rounded-full bg-black/5 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              </div>
              <span className="text-lg font-semibold text-gray-900 tracking-tight transition-colors duration-200 group-hover:text-black">
                EmployMeGlobal
              </span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden items-center gap-8 md:flex">
            <div className="flex items-center gap-1">
              <a
                href="#demo"
                className="group relative px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-gray-900"
              >
                <span className="relative z-10">Interview flow</span>
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-black to-gray-800 transition-all duration-300 group-hover:w-full"></span>
                <span className="absolute inset-0 bg-gray-50 scale-0 rounded-lg transition-transform duration-300 group-hover:scale-100 -z-10"></span>
              </a>
              <a
                href="#blog"
                className="group relative px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-gray-900"
              >
                <span className="relative z-10">Blog</span>
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-black to-gray-800 transition-all duration-300 group-hover:w-full"></span>
                <span className="absolute inset-0 bg-gray-50 scale-0 rounded-lg transition-transform duration-300 group-hover:scale-100 -z-10"></span>
              </a>
              <a
                href="#cost-saving-calculator"
                className="group relative px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-gray-900"
              >
                <span className="relative z-10">Cost saving calculator</span>
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-black to-gray-800 transition-all duration-300 group-hover:w-full"></span>
                <span className="absolute inset-0 bg-gray-50 scale-0 rounded-lg transition-transform duration-300 group-hover:scale-100 -z-10"></span>
              </a>
              <Link
                to="/careers"
                className="group relative px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-gray-900"
              >
                <span className="relative z-10">Job posts</span>
                <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-black to-gray-800 transition-all duration-300 group-hover:w-full"></span>
                <span className="absolute inset-0 bg-gray-50 scale-0 rounded-lg transition-transform duration-300 group-hover:scale-100 -z-10"></span>
              </Link>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/login"
                className="px-4 py-2.5 text-sm font-medium text-gray-700 transition-all duration-300 hover:text-gray-900 hover:scale-105"
              >
                Login
              </Link>
              <Link
                to="/signup?role=job_seeker"
                className="group relative overflow-hidden rounded-lg bg-[#00A5E6] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all duration-300 hover:bg-[#008FCC] hover:shadow-lg hover:scale-105"
              >
                <span className="relative z-10">Job seeker sign up</span>
                <span className="absolute inset-0 bg-gradient-to-r from-[#00A5E6] to-[#008FCC] opacity-0 transition-opacity duration-300 group-hover:opacity-100"></span>
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden flex flex-col gap-1.5 p-2 rounded-lg transition-all duration-300 hover:bg-gray-100"
            aria-label="Toggle menu"
          >
            <span className={`h-0.5 w-6 bg-gray-900 transition-all duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
            <span className={`h-0.5 w-6 bg-gray-900 transition-all duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
            <span className={`h-0.5 w-6 bg-gray-900 transition-all duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
          </button>
        </div>

        {/* Mobile Menu */}
        <div className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'
        }`}>
            <div className="px-4 py-4 space-y-2 border-t border-gray-200 bg-white">
            <a
              href="#demo"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-gray-700 rounded-lg transition-all duration-200 hover:bg-gray-50 hover:text-gray-900"
            >
              Interview flow
            </a>
            <a
              href="#blog"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-gray-700 rounded-lg transition-all duration-200 hover:bg-gray-50 hover:text-gray-900"
            >
              Blog
            </a>
            <a
              href="#cost-saving-calculator"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-gray-700 rounded-lg transition-all duration-200 hover:bg-gray-50 hover:text-gray-900"
            >
              Cost saving calculator
            </a>
            <Link
              to="/careers"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-sm font-medium text-gray-700 rounded-lg transition-all duration-200 hover:bg-gray-50 hover:text-gray-900"
            >
              Job posts
            </Link>
            <div className="pt-2 space-y-2 border-t border-gray-200">
              <Link
                to="/login"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-medium text-gray-700 rounded-lg transition-all duration-200 hover:bg-gray-50 hover:text-gray-900"
              >
                Login
              </Link>
              <Link
                to="/signup?role=job_seeker"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-4 py-3 text-sm font-semibold text-white bg-[#00A5E6] rounded-lg text-center transition-all duration-200 hover:bg-[#008FCC]"
              >
                Job seeker sign up
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2">
          <div className="flex flex-col justify-center px-6 py-12 sm:px-10 lg:px-12">
            <div className="mx-auto w-full max-w-2xl">
              <h1 className="text-4xl font-bold leading-tight tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Automate hiring with AI
                <span className="block text-black"> from interview to offer</span>
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-gray-600 sm:text-xl">
                Employ Me Global runs structured interviews, scores assessments and generates offers automatically, so your team can hire better people with less manual work.
                Post roles, let our AI screen and schedule, and track every candidate from a single, simple dashboard.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  to="/signup?role=employer"
                  className="rounded-lg bg-[#00A5E6] px-6 py-3 text-center text-base font-semibold text-white transition-colors hover:bg-[#008FCC]"
                >
                  I'm an Employer
                </Link>
                <Link
                  to="/signup?role=job_seeker"
                  className="rounded-lg border-2 border-[#00A5E6] bg-white px-6 py-3 text-center text-base font-semibold text-[#00A5E6] transition-colors hover:bg-[#e6f7fc]"
                >
                  I'm a Job Seeker
                </Link>
              </div>
            </div>
          </div>
          <div className="hidden lg:block">
            <MedicalDecor />
          </div>
        </div>
      </section>

      {/* Open Jobs */}
      <section id="jobs" className="border-t border-gray-200 bg-gray-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-12">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">Open job posts</h2>
              <p className="mt-3 text-lg text-gray-600">
                Browse live roles and share opportunities with your network.
              </p>
            </div>
            <Link
              to="/careers"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#00A5E6] hover:text-[#008FCC]"
            >
              View all jobs
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>

          <div className="mt-10">
            {jobsLoading && (
              <p className="text-sm text-gray-500">Loading open jobs...</p>
            )}
            {jobsError && (
              <p className="text-sm text-red-600">{jobsError}</p>
            )}
            {!jobsLoading && !jobsError && jobs.length === 0 && (
              <p className="text-sm text-gray-500">No open jobs right now. Check back soon.</p>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              {jobs.map((job) => (
                <div
                  key={job._id}
                  className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <Link
                        to={`/careers/${job._id}`}
                        className="text-lg font-semibold text-gray-900 hover:text-[#00A5E6]"
                      >
                        {job.title}
                      </Link>
                      <p className="mt-1 text-sm text-gray-500">
                        {job.location?.city}, {job.location?.state} · {locationTypeLabel(job.locationType)}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                      {employmentTypeLabel(job.employmentType)}
                    </span>
                  </div>
                  {job.description ? (
                    <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-gray-600">
                      {job.description}
                    </p>
                  ) : null}
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/careers/${job._id}`}
                      className="text-sm font-semibold text-[#00A5E6] hover:text-[#008FCC]"
                    >
                      View job
                    </Link>
                    <JobShareButton jobId={job._id} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CostSavingCalculator />

      {/* Demo Section */}
      <section id="demo" className="bg-gray-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              How Employ Me works
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              From posting a role to AI‑driven offers in days
            </p>
          </div>

          <div className="mt-12">
            <div className="mx-auto max-w-5xl">
              {/* Demo Card - Browser Window */}
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
                {/* Browser Header */}
                <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="h-3 w-3 rounded-full bg-red-500"></div>
                      <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                      <div className="h-3 w-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="ml-4 flex-1 rounded-md bg-white px-4 py-1.5 text-xs text-gray-600 border border-gray-300">
                      employme.jobs/dashboard
                    </div>
                  </div>
                </div>

                {/* Demo Content */}
                <div className="p-8 bg-gradient-to-br from-gray-50 to-white">
                  <div className="space-y-6">
                    {/* Step Indicator */}
                    <div className="flex items-center justify-center gap-2 mb-8">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white text-xs font-semibold">1</div>
                        <span className="text-xs font-medium text-gray-600">Create Job</span>
                      </div>
                      <div className="w-8 h-0.5 bg-gray-300"></div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white text-xs font-semibold">2</div>
                        <span className="text-xs font-medium text-gray-600">Applicants</span>
                      </div>
                      <div className="w-8 h-0.5 bg-gray-300"></div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white text-xs font-semibold">3</div>
                        <span className="text-xs font-medium text-gray-600">Interviews</span>
                      </div>
                      <div className="w-8 h-0.5 bg-gray-300"></div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white text-xs font-semibold">4</div>
                        <span className="text-xs font-medium text-gray-600">Hire</span>
                      </div>
                    </div>

                    {/* Job Card */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">Step 1: Post a job</h4>
                      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="mb-3 flex items-center gap-3">
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-800">
                                New Role
                              </span>
                              <span className="text-xs text-gray-500">2 hours ago</span>
                            </div>
                            <h3 className="text-base font-semibold text-gray-900 mb-2">
                              Customer Success Manager
                            </h3>
                            <p className="text-sm text-gray-600 leading-relaxed">
                              We are hiring an experienced customer success manager to lead onboarding and support for key accounts. You will run structured check‑ins, track product adoption and keep renewal risks visible in one place.
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 flex items-center gap-6 border-t border-gray-100 pt-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span className="font-medium">24 Applicants</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="font-medium">From ₦350,000 / month</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Applicant Comparison Cards */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">Step 2: Review applicants</h4>
                      <div className="grid gap-4 sm:grid-cols-3">
                        <div className="rounded-lg border-2 border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-md">
                          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Candidate A
                          </div>
                          <div className="mb-2 text-2xl font-bold text-gray-900">3 yrs</div>
                          <div className="mb-4 text-xs text-gray-600">Experience supporting mid‑market B2B customers</div>
                          <div className="space-y-1.5 text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Excellent stakeholder communication</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Comfortable with rotating schedules</span>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border-2 border-black bg-gray-50 p-5 shadow-lg transition-all hover:shadow-xl">
                          <div className="mb-3 flex items-center justify-between">
                            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Candidate B
                            </div>
                            <span className="rounded-full bg-black px-2 py-0.5 text-xs font-semibold text-white">
                              Shortlisted
                            </span>
                          </div>
                          <div className="mb-2 text-2xl font-bold text-gray-900">5 yrs</div>
                          <div className="mb-4 text-xs text-gray-600">Leads cross‑functional teams in fast‑growing companies</div>
                          <div className="space-y-1.5 text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Coaches and mentors teammates</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Strong CRM and analytics skills</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Available within 2 weeks</span>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border-2 border-gray-200 bg-white p-5 transition-all hover:border-gray-300 hover:shadow-md">
                          <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                            Candidate C
                          </div>
                          <div className="mb-2 text-2xl font-bold text-gray-900">2 yrs</div>
                          <div className="mb-4 text-xs text-gray-600">Rising talent with strong references</div>
                          <div className="space-y-1.5 text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Great bedside manner</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Comfortable with rotating shifts</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <svg className="h-3 w-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Open to relocation</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Interview & Offer Card */}
                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">Step 3: AI‑assisted interviews & offers</h4>
                      <div className="rounded-lg border-2 border-gray-200 bg-white p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-black text-white">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900">Interview scheduled</h5>
                              <p className="text-xs text-gray-500">Candidate has been invited with an AI‑generated interview kit</p>
                            </div>
                          </div>
                          <span className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white">
                            Scheduled
                          </span>
                        </div>
                        <div className="space-y-3 border-t border-gray-100 pt-4">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Role</span>
                            <span className="text-sm font-semibold text-gray-900">Customer Success Manager</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Location</span>
                            <span className="text-sm font-semibold text-gray-900">Lagos • hybrid</span>
                          </div>
                          <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                            <span className="text-sm font-semibold text-gray-900">Interview date</span>
                            <span className="text-base font-bold text-gray-900">March 15, 2024 • 2:30 PM</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>We keep every interview, scorecard and offer organised and auditable</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Hire Status Card */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-4">Step 4: Hire confirmed</h4>
                      <div className="rounded-lg border-2 border-gray-200 bg-white p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-black text-white">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <div>
                              <h5 className="text-sm font-semibold text-gray-900">Offer accepted</h5>
                              <p className="text-xs text-gray-500">Your new hire is ready to start, with paperwork handled automatically</p>
                            </div>
                          </div>
                          <span className="rounded-full bg-gray-900 px-3 py-1 text-xs font-semibold text-white">
                            Delivered
                          </span>
                        </div>
                        <div className="space-y-3 border-t border-gray-100 pt-4">
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">Acme Technologies</p>
                              <p className="text-xs text-gray-500">123 Innovation Way, Lagos</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">Customer Success Manager</p>
                              <p className="text-xs text-gray-500">Full‑time role with a structured onboarding plan</p>
                            </div>
                          </div>
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-gray-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">Start date: March 16, 2024</p>
                              <p className="text-xs text-gray-500">All approvals, contracts and signatures completed</p>
                            </div>
                          </div>
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <button className="w-full rounded-lg bg-[#00A5E6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#008FCC] transition-colors">
                            View in employer dashboard
                          </button>
                        </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Automation Guides Section */}
      <section id="blog" className="bg-gray-50 py-16 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Playbooks for automated hiring
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Short, practical guides on job posts, interview automation and offer workflows.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* Guide 1 */}
            <article className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="border-b border-gray-100 bg-gradient-to-r from-indigo-50 via-sky-50 to-white px-5 py-4">
                <span className="inline-flex items-center rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                  Job posts
                </span>
                <h3 className="mt-3 text-lg font-bold text-gray-900 group-hover:text-black">
                  Write one job post that powers your whole pipeline
                </h3>
              </div>
              <div className="flex flex-1 flex-col justify-between px-5 py-5">
                <p className="text-sm text-gray-600">
                  Turn a single, well‑structured job description into scorecards, interview questions and offer templates that stay consistent across your team.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                    <span>Tag must‑have vs nice‑to‑have skills for AI screening.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-500" />
                    <span>Auto‑generate interview questions from your requirements.</span>
                  </li>
                </ul>
              </div>
            </article>

            {/* Guide 2 */}
            <article className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="border-b border-gray-100 bg-gradient-to-r from-sky-50 via-blue-50 to-white px-5 py-4">
                <span className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                  Interview automation
                </span>
                <h3 className="mt-3 text-lg font-bold text-gray-900 group-hover:text-black">
                  Automate scheduling and structured interviews
                </h3>
              </div>
              <div className="flex flex-1 flex-col justify-between px-5 py-5">
                <p className="text-sm text-gray-600">
                  Replace back‑and‑forth emails with smart scheduling links, shared interview kits and real‑time scorecards.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                    <span>Offer candidates time slots that respect interviewer calendars.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                    <span>Capture consistent notes and ratings for every conversation.</span>
                  </li>
                </ul>
              </div>
            </article>

            {/* Guide 3 */}
            <article className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="border-b border-gray-100 bg-gradient-to-r from-emerald-50 via-teal-50 to-white px-5 py-4">
                <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                  Offers & compliance
                </span>
                <h3 className="mt-3 text-lg font-bold text-gray-900 group-hover:text-black">
                  Generate offers and keep every hire auditable
                </h3>
              </div>
              <div className="flex flex-1 flex-col justify-between px-5 py-5">
                <p className="text-sm text-gray-600">
                  Use templates and approvals to move from verbal “yes” to signed offer without losing track of budgets, documents or start dates.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    <span>Draft compliant offers in a few clicks.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    <span>Keep every scorecard, approval and contract in one trail.</span>
                  </li>
                </ul>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-white py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Teams hiring faster with automation
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Stories from people who replaced manual interviews and job posts with a single automated flow.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {/* Testimonial 1 - Head of Talent */}
            <div className="relative bg-white rounded-2xl border border-gray-200 p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="absolute top-6 right-6 opacity-10">
                <svg className="w-16 h-16 text-indigo-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.996 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.984zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
              </div>

              <div className="relative">
                <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wide text-indigo-600">
                  <span className="inline-flex h-2 w-2 rounded-full bg-indigo-600" />
                  Head of Talent
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  “We used to manage interviews in spreadsheets. With Employ Me, jobs, screening, interviews and offers live in one workflow. Time‑to‑hire dropped from 28 days to 10 days.”
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#00A5E6] text-lg font-semibold text-white shadow-md">
                      TO
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Tunde Okeke</div>
                      <div className="text-sm text-gray-500">Head of Talent, GrowthCare</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-semibold text-gray-500">Time‑to‑hire</div>
                    <div className="text-sm font-bold text-gray-900">-64%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 - Recruiter */}
            <div className="relative rounded-xl border border-sky-200 bg-sky-50/60 p-8 shadow-sm transition-shadow duration-300 hover:shadow-md">
              <div className="absolute top-6 right-6 opacity-10">
                <svg className="w-16 h-16 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.996 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.984zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
              </div>

              <div className="relative">
                <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wide text-blue-700">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  In‑house recruiter
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  “I publish a job once, and the platform handles screening, interview slots and reminders. I spend my time speaking with the right candidates instead of chasing calendars.”
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-lg shadow-md">
                      AN
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Amara Nwosu</div>
                      <div className="text-sm text-gray-500">Recruiter, City Clinic</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-semibold text-gray-500">Manual emails</div>
                    <div className="text-sm font-bold text-gray-900">-80%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 - Candidate */}
            <div className="relative bg-white rounded-2xl border border-gray-200 p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="absolute top-6 right-6 opacity-10">
                <svg className="w-16 h-16 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.996 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.984zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
              </div>

              <div className="relative">
                <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wide text-emerald-600">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  Candidate
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  “Every stage of my interview was clear: I could see which tasks were done, what was next and when to expect feedback. The AI tips before each call made me feel prepared.”
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-semibold text-lg shadow-md">
                      LK
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">Lilian Kalu</div>
                      <div className="text-sm text-gray-500">Customer Success, Lagos</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs font-semibold text-gray-500">Experience rating</div>
                    <div className="text-sm font-bold text-gray-900">4.9 / 5</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cookie Banner */}
      {showCookieBanner && (
        <div className="fixed inset-x-0 bottom-0 z-40">
          <div className="mx-auto max-w-7xl px-4 pb-4 sm:px-6 lg:px-8">
            <div className="rounded-2xl bg-white/95 shadow-lg border border-gray-200 px-4 py-4 sm:px-6 sm:py-5 backdrop-blur">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-gray-900">
                    We use cookies to improve your experience
                  </p>
                  <p className="text-xs text-gray-600">
                    Employ Me uses essential and analytics cookies to keep the platform secure, remember your preferences, and understand how the site is used. You can learn more in our{" "}
                    <Link to="/cookie-policy" className="font-medium text-blue-600 hover:underline">
                      Cookie Policy
                    </Link>
                    .
                  </p>
                </div>
                <div className="flex flex-row items-center justify-end gap-3 mt-1 sm:mt-0 flex-nowrap">
                  <Link
                    to="/privacy-policy"
                    className="text-xs font-medium text-gray-600 hover:text-gray-900 hover:underline whitespace-nowrap"
                  >
                    Privacy Policy
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      try {
                        window.localStorage.setItem('employme_cookie_consent', 'accepted')
                      } catch (e) {
                        // ignore
                      }
                      setShowCookieBanner(false)
                    }}
                    className="inline-flex items-center justify-center rounded-lg bg-[#00A5E6] px-4 py-2 text-xs font-semibold text-white hover:bg-[#008FCC] whitespace-nowrap"
                  >
                    Accept cookies
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50">
        <div className="mx-auto max-w-7xl px-6 py-12 sm:px-10 lg:px-12">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <img className="h-8 w-8" src={logo} alt="Employ Me logo" />
                <span className="text-lg font-semibold text-gray-900 tracking-tight">EmployMeGlobal</span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Automating hiring for modern teams with AI‑powered interviews, assessments and offers.
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="Twitter">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
                <a href="#" className="text-gray-400 hover:text-gray-600 transition-colors" aria-label="LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Quick Links
              </h3>
              <ul className="space-y-3">
                <li>
                  <a href="#demo" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Interview flow
                  </a>
                </li>
                <li>
                  <a href="#blog" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Blog
                  </a>
                </li>
                <li>
                  <a href="#cost-saving-calculator" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Cost saving calculator
                  </a>
                </li>
              </ul>
            </div>

            {/* For Users */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                For Users
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/#jobs" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Browse jobs
                  </Link>
                </li>
                <li>
                  <Link to="/signup?role=job_seeker" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Job seeker signup
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Employer & hospital login
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Contact support
                  </Link>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Legal
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/privacy-policy" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/cookie-policy" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Cookie Policy
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-gray-600 hover:text-gray-900 transition-colors">
                    Contact Us
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex flex-col items-center justify-center">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} Employ Me. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Landing

